/**
 * Shared date/time building blocks
 *
 * Single source of truth for day/month arrays and 12-hour time formatting.
 * Used by formatRelativeTime (ladder display) and absolute formatters in locationFormatters.js.
 */

import { ACTIVITY_STATUS_HIDE_DAYS } from './lifecycleWindows';

export const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Format a Date to 12-hour time string
 * @param {Date} date
 * @param {Object} [options]
 * @param {boolean} [options.dropMinutesOnHour=false] - Return "5pm" instead of "5:00pm" when minutes are 0
 * @param {boolean} [options.useUTC=false] - Read hours/minutes via getUTC* (for offset-shifted dates)
 * @returns {string} e.g. "4:30pm", "12:00am", or "5pm" (when dropMinutesOnHour)
 */
export const formatTime12h = (date, { dropMinutesOnHour = false, useUTC = false } = {}) => {
  let hours = useUTC ? date.getUTCHours() : date.getHours();
  const minutes = useUTC ? date.getUTCMinutes() : date.getMinutes();
  const period = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;

  if (dropMinutesOnHour && minutes === 0) {
    return `${hours}${period}`;
  }

  return `${hours}:${minutes.toString().padStart(2, '0')}${period}`;
};

/**
 * Friendly approximate time (Anon WE MET card, decision 2026-07-18): rounded
 * to the nearest half hour, minutes dropped on the hour, NO colon — "2pm" /
 * "230pm". Shows off that the when was logged without the offputting
 * minute-precision. Rounding never crosses midnight (the date is part of the
 * record): the last stretch of the day clamps to 1130pm.
 *
 * @param {Date} date - The instant (possibly pre-shifted for wall-clock reads)
 * @param {boolean} [options.useUTC] - Read via getUTC* getters (shifted dates)
 * @returns {string}
 */
export const formatFriendlyTime12h = (date, { useUTC = false } = {}) => {
  const hours = useUTC ? date.getUTCHours() : date.getHours();
  const minutes = useUTC ? date.getUTCMinutes() : date.getMinutes();
  let total = Math.round((hours * 60 + minutes) / 30) * 30;
  if (total >= 1440) total = 1410; // clamp: stay on the meeting's calendar day
  const h24 = Math.floor(total / 60);
  const m = total % 60;
  const period = h24 >= 12 ? 'pm' : 'am';
  const h12 = h24 % 12 || 12;
  return m === 0 ? `${h12}${period}` : `${h12}${m}${period}`;
};

/**
 * The device's current UTC offset in minutes EAST of UTC (e.g. EDT → -240,
 * India → +330). Captured at meeting creation to pin display to meeting-local
 * time. Note the sign flip: JS getTimezoneOffset() returns minutes WEST.
 */
export const currentUtcOffsetMinutes = () => -new Date().getTimezoneOffset();

/**
 * Formats an ISO timestamp to relative time for connection list display
 *
 * Ladder:
 * - "Just now" (< 1 min)
 * - "Xm ago" (< 60 min)
 * - "Xh ago" (< 24 hours, same day)
 * - "Yesterday"
 * - Day name "Mon" (< 7 days)
 * - "M/D" (< 1 year)
 * - "M/D/YY" (1+ year)
 */
export const formatRelativeTime = (isoString) => {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);
    const now = new Date();

    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    // Day boundaries
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    if (diffHours < 24 && dateDay.getTime() === today.getTime()) {
      return `${diffHours}h ago`;
    }

    if (dateDay.getTime() === yesterday.getTime()) return 'Yesterday';

    const diffDays = Math.floor((today - dateDay) / (1000 * 60 * 60 * 24));

    if (diffDays < 7) {
      return DAYS_SHORT[date.getDay()];
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();

    // 1+ year: include 2-digit year
    if (date.getFullYear() !== now.getFullYear() && diffDays >= 365) {
      const year = date.getFullYear() % 100;
      return `${month}/${day}/${year.toString().padStart(2, '0')}`;
    }

    return `${month}/${day}`;
  } catch {
    return '';
  }
};

/**
 * Format a last_active_at timestamp into the activity time ladder.
 * Returns null when no status should display (null input or 14+ days inactive).
 *
 * | Age        | Return value                                  |
 * |------------|----------------------------------------------|
 * | null       | null                                          |
 * | < 60 min   | { text: 'Active Xm ago', isActive: false }    |
 * | 1–23 hrs   | { text: 'Active Xh ago', isActive: false }    |
 * | 1–13 days  | { text: 'Active X days ago', isActive: false } |
 * | 14+ days   | null                                          |
 *
 * @param {string|null} isoTimestamp
 * @returns {{ text: string, isActive: boolean } | null}
 */
export const formatActivityStatus = (isoTimestamp) => {
  if (!isoTimestamp) return null;

  try {
    const date = new Date(isoTimestamp);
    const now = Date.now();
    const diffMs = now - date.getTime();

    if (diffMs < 0) return { text: 'Active 1m ago', isActive: false }; // clock skew safety

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return { text: `Active ${Math.max(1, diffMins)}m ago`, isActive: false };
    if (diffHours < 24) return { text: `Active ${diffHours}h ago`, isActive: false };
    if (diffDays < ACTIVITY_STATUS_HIDE_DAYS)
      return { text: `Active ${diffDays} day${diffDays === 1 ? '' : 's'} ago`, isActive: false };

    return null; // 14+ days — silence is the signal (ACTIVITY_STATUS_HIDE_DAYS)
  } catch {
    return null;
  }
};
